//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useTranslation } from 'react-i18next';

interface SolutionCardProps {
  appName: string;
  title: string;
  text: string;
  content: string;
  createdAt: string;
  link: string;
  nameLabel: string;
  plugintype: string;
  buttonLink: string;
  updated: string;
  provider: string;
  imageUrl: string;
  onViewDetails?: () => void;
}

function SolutionCard({
  title,
  content,
  link,
  nameLabel,
  imageUrl,
  onViewDetails,
}: SolutionCardProps) {
  const { t } = useTranslation();

  const tagColor = (() => {
    switch (nameLabel.toLowerCase()) {
      case 'measure':
        return 'bg-[#24A148] text-white';
      case 'deploy':
        return 'bg-[#0F62FE] text-white';
      case 'plan':
        return 'bg-[#8A3FFC] text-white';
      case 'build':
        return 'bg-[#F1C21B] text-gray-900';
      default:
        return 'bg-gray-500 text-white';
    }
  })();

  return (
    <div
      id="plugin-card-container"
      data-testid="plugin-card-container"
      className="flex h-[280px] w-full flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#262626]"
    >
      {/* Header: Logo + Tag badge */}
      <div
        id="id-plugin-card-header"
        data-testid="plugin-card-header"
        className="mb-4 flex items-start justify-between"
      >
        <img
          src={imageUrl}
          alt={`${title} icon`}
          className="h-6 max-w-[50%] object-contain object-left"
        />
        <span
          id="id-plugin-card-tag"
          data-testid="plugin-card-tag"
          className={`rounded px-2 py-0.5 text-xs font-medium ${tagColor}`}
        >
          {t(nameLabel)}
        </span>
      </div>

      {/* Content: Title + Description */}
      <div
        id="id-plugin-card-content"
        data-testid="plugin-card-content"
        className="mb-4"
      >
        <h3 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
          {title}
        </h3>
        <p className="line-clamp-6 text-xs leading-relaxed text-gray-600 dark:text-gray-400">
          {content}
        </p>
      </div>

      {/* Footer: View Details link */}
      <div
        id="id-plugin-card-footer"
        data-testid="plugin-card-footer"
        className="mt-auto flex justify-end pt-2"
      >
        <button
          onClick={onViewDetails}
          aria-label={`${t('viewDetails')} ${title}`}
          className="cursor-pointer text-xs text-[#0F62FE] hover:underline dark:text-[#78A9FF]"
        >
          {t('viewDetails')}
        </button>
      </div>
    </div>
  );
}

export default SolutionCard;
